import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMutation, useQuery } from '@apollo/client';
import { Alert, Card, CardContent, CheckboxInput, Select } from '@guildeducationinc/recess';
import EditIcon from '@guildeducationinc/recess/icons/EditIcon';
import { AdminTopNav } from '@src/components/Admin/AdminTopNav';
import { CountrySelect } from '@src/components/CountrySelect';
import { SAVE_CLIENT_PROGRAM_LEVEL, UPDATE_CLIENT_PROGRAM_LEVELS, } from '@src/graphql/queries/clientProgramLevel';
import { COUNTRIES } from '@src/graphql/queries/lookup';
import { useClientIdParam } from '@src/hooks/useClientIdParam';
import { useClientProgramLevels } from '@src/hooks/useClientProgramLevels';
import { ProgramLevelType } from '@src/types';
import { pick, sortBy } from 'lodash';
import { DataTable, Form, SearchFilterBar, TrackableButton, useAlert, useLocale, useSessionState, } from 'pe-core-frontend';
import { useMemo, useState } from 'react';
import styles from './ClientProgramLevels.module.scss';
export const ClientProgramLevels = () => {
    const clientId = useClientIdParam() || 0;
    const { t } = useLocale();
    const { showAlert } = useAlert();
    const [searchText, setSearchText] = useSessionState(`clientProgramLevelsSearch-${clientId}`, '');
    const [countryCode, setCountryCode] = useSessionState(`clientProgramLevelsCountryFilter-${clientId}`, null);
    const [programLevelType, setProgramLevelType] = useSessionState(`clientProgramLevelsTypeFilter-${clientId}`, null);
    const [showActive, setShowActive] = useSessionState(`clientProgramLevelsShowActive-${clientId}`, true);
    const [showInactive, setShowInactive] = useSessionState(`clientProgramLevelsShowInactive-${clientId}`, true);
    // Determine is_active filter based on checkbox states
    const isActiveFilter = showActive && showInactive ? undefined : showActive ? true : showInactive ? false : undefined;
    const { clientProgramLevels, loading, error, page, pageSize, setPage, setPageSize, total, orderBy, orderByDir, setOrderBy, setOrderByDir, refetch, } = useClientProgramLevels({
        clientId,
        searchText,
        countryCode,
        isActive: isActiveFilter,
        type: programLevelType,
    });
    const [save] = useMutation(SAVE_CLIENT_PROGRAM_LEVEL);
    const [updateClientProgramLevels] = useMutation(UPDATE_CLIENT_PROGRAM_LEVELS);
    const [selectedLevel, setSelectedLevel] = useState(null);
    const [saveError, setSaveError] = useState('');
    // Get Country Options
    const { data: countriesData } = useQuery(COUNTRIES, {
        variables: { includeInactive: false },
    });
    const countryOptions = countriesData?.countries.map((c) => ({
        id: c.params.id,
        code: c.value,
        name: c.label,
    }));
    const Fields = useMemo(() => {
        const fields = [
            {
                name: 'isActive',
                label: t('Active?'),
                type: 'boolean',
                required: true,
                serializeValue: (value) => (value ? 'true' : 'false'),
                deserializeValue: (value) => value === 'true',
            },
            {
                name: 'type',
                label: t('Program Level Type'),
                type: 'lookup',
                required: true,
                options: Object.keys(ProgramLevelType).map(type => ({
                    value: type,
                    label: t(`programLevelType.${type}`),
                })),
            },
        ];
        return fields;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const handleValidate = (clientProgramLevel) => {
        const errors = {};
        if (!clientProgramLevel.type) {
            errors.type = t('required');
        }
        return errors;
    };
    const handleSave = async (clientProgramLevel) => {
        try {
            setSaveError('');
            const result = await save({
                variables: {
                    input: {
                        ...pick(clientProgramLevel, ['id', 'isActive', 'type']),
                    },
                },
            });
            if (result.data?.saveClientProgramLevel?.success) {
                await refetch();
                const message = result.data.saveClientProgramLevel.messages[0];
                const toShow = t(message.value, { values: message.params });
                showAlert(toShow, 'success');
                setSelectedLevel(null);
            }
            else {
                const message = result.data.saveClientProgramLevel.messages[0];
                const toShow = t(message.value, { values: message.params });
                showAlert(toShow, 'error');
            }
        }
        catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
            throw new Error(t('Unable to save program level'));
        }
    };
    const handleSortModelChange = (sortModel) => {
        if (sortModel && sortModel.length > 0) {
            const sort = sortModel[0];
            setOrderBy(sort.field);
            setOrderByDir(sort.sort);
        }
        else {
            setOrderBy(null);
            setOrderByDir(null);
        }
    };
    const handleUpdateSearchResult = async (isActive) => {
        try {
            const result = await updateClientProgramLevels({
                variables: {
                    input: { isActive },
                    spec: {
                        clientId,
                        name: searchText || undefined,
                        countryCode: countryCode || undefined,
                        isActive: isActiveFilter,
                        type: programLevelType || undefined,
                    },
                },
            });
            if (result.data?.updateClientProgramLevels &&
                !result.data.updateClientProgramLevels.success) {
                return result.data.updateClientProgramLevels;
            }
            await refetch();
            const message = result.data?.updateClientProgramLevels.messages[0];
            if (message) {
                showAlert(message.value, 'success');
            }
        }
        catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
            showAlert(t('Unable to update client program levels'), 'error');
        }
    };
    return (_jsxs("div", { children: [_jsx(AdminTopNav, { title: 'Program levels', searchFilterActions: _jsx(SearchFilterBar, { enableSearch: true, enableFilter: true, searchText: searchText, onSearchChange: text => {
                        setSearchText(text);
                        setPage(1);
                    }, searchPlaceholder: 'Search by Name', filterActions: _jsxs("div", { className: styles.filterActions, children: [_jsxs("div", { className: styles.filterGroup, children: [_jsx(CheckboxInput, { label: 'Active', checked: showActive, onChange: e => {
                                            setShowActive(e.target.checked);
                                            setPage(1);
                                        } }), _jsx(CheckboxInput, { label: 'Inactive', checked: showInactive, onChange: e => {
                                            setShowInactive(e.target.checked);
                                            setPage(1);
                                        } })] }), _jsx(CountrySelect, { label: 'Country', className: styles.countrySelect, value: countryCode || '', onChange: code => {
                                    setCountryCode(code || null);
                                    setPage(1);
                                }, supportEmptyState: true, options: sortBy(countryOptions || [], 'name').map((c) => ({
                                    value: c.code,
                                    label: c.name,
                                })) }), _jsx(Select, { label: 'Type', className: styles.countrySelect, value: programLevelType || '', onChange: e => {
                                    setProgramLevelType(e.target.value || null);
                                    setPage(1);
                                }, options: [
                                    { value: '', label: 'All' },
                                    ...Object.values(ProgramLevelType).map(type => ({
                                        value: type,
                                        label: t(`programLevelType.${type}`),
                                    })),
                                ] })] }), isFilterActive: !!countryCode || !!programLevelType || !showActive || !showInactive }) }), _jsx("div", { children: _jsx(Card, { children: _jsxs(CardContent, { children: [error && (_jsx(Alert, { display: true, hideClose: true, type: 'danger', children: error.message })), total > 0 && !loading && (_jsxs("div", { className: 'mb-3 flex gap-1', children: [_jsx(TrackableButton, { btnType: 'mui', enText: 'Make Inactive', tag: 'make_inactive', size: 'small', variant: 'outlined', onClick: () => handleUpdateSearchResult(false), children: t('Make {count} Inactive', { values: { count: total } }) }), _jsx(TrackableButton, { btnType: 'mui', enText: 'Make Active', tag: 'make_active', size: 'small', variant: 'outlined', onClick: () => handleUpdateSearchResult(true), children: t('Make {count} Active', { values: { count: total } }) })] })), _jsx(DataTable, { disableRowSelectionOnClick: true, disableColumnMenu: true, rows: clientProgramLevels, loading: loading, pageSizeOptions: [10, 25], paginationMode: 'server', paginationModel: { page: page - 1, pageSize }, onPaginationModelChange: pagination => {
                                    setPage(pagination.page + 1);
                                    setPageSize(pagination.pageSize);
                                }, onSortModelChange: handleSortModelChange, sortModel: [{ field: orderBy ?? 'name', sort: orderByDir }], rowCount: total, columns: [
                                    {
                                        field: 'programLevel.name',
                                        headerName: t('Name'),
                                        flex: 0.25,
                                        renderCell: ({ row }) => row.programLevel.name,
                                    },
                                    {
                                        field: 'numCountries',
                                        headerName: t('Num Countries'),
                                        width: 150,
                                        valueGetter: params => params.row.programLevel?.countries?.length || 0,
                                        sortable: true,
                                    },
                                    {
                                        field: 'countries',
                                        headerName: t('Countries'),
                                        flex: 0.25,
                                        renderCell: ({ row }) => {
                                            const countries = row.programLevel?.countries || [];
                                            const names = countries.map((c) => c.name);
                                            const shown = names.slice(0, 5).join(', ');
                                            const suffix = names.length > 5 ? ', ...' : '';
                                            return `${shown}${suffix}`;
                                        },
                                        sortable: false,
                                    },
                                    {
                                        field: 'isActive',
                                        headerName: t('Active?'),
                                        valueGetter: e => (e.row.isActive ? 'Yes' : 'No'),
                                        width: 100,
                                        sortable: true,
                                    },
                                    {
                                        field: 'type',
                                        headerName: t('Program Level Type'),
                                        width: 100,
                                        renderCell: ({ value }) => _jsx(_Fragment, { children: t(`programLevelType.${value}`) }),
                                    },
                                    {
                                        field: 'action',
                                        headerName: t('Actions'),
                                        renderCell: ({ row }) => (_jsx("div", { children: _jsx(TrackableButton, { btnType: 'icon', enText: 'Edit', tag: `clientProgramLevel_${row.id}`, label: t('Edit'), appearance: 'minimal', icon: _jsx(EditIcon, { size: 20 }), onClick: () => setSelectedLevel(row), className: 'margin--right_8' }) })),
                                        width: 100,
                                        sortable: false,
                                    },
                                ] }), selectedLevel && (_jsx(Form, { title: 'Edit program level', modal: true, obj: selectedLevel, fields: Fields, onValidate: handleValidate, onSave: handleSave, onCancel: () => {
                                    setSelectedLevel(null);
                                    setSaveError('');
                                }, error: saveError }))] }) }) })] }));
};
